import type { DayNightPhase } from "../models/dayNightPhase.ts";
import type { GamePhase } from "../models/phase.ts";
import type { Season } from "../models/season.ts";
import type { TileTerrain, Weather, WorldState } from "../models/worldState.ts";
import { tileKey } from "../models/worldState.ts";
import {
  type AmbientPredatorLevel,
  getTileAmbientPredatorLevel,
} from "../reducers/economy/tileAmbientPredators.ts";
import { getWaterAmount } from "../reducers/economy/tileWater.ts";
import { resolveTemperature } from "../reducers/world/resolveTemperature.ts";
import {
  getCycleProgressForWeatherTurn,
  getDayNightPhaseForWeatherTurn,
} from "../reducers/world/weatherCalendar.ts";

/**
 * Shell HUD view for hosts until the Babylon DOM HUD is split into per-surface
 * modules. Then replace this kitchen-sink with smaller selectors (stats panel,
 * phase hint, setup/settle, outcome) matched to each UI element.
 */
export interface SetupHudView {
  readonly phase: GamePhase;
  readonly turn: number;
  readonly clanName: string;
  readonly population: number;
  readonly foodSupplies: number;
  readonly tileCount: number;
  readonly revealedTileCount: number;
  readonly canSelectTotem: boolean;
  readonly canChooseStart: boolean;
  readonly canCompleteSettle: boolean;
  readonly canEndTurn: boolean;
  readonly hint: string;
  readonly season: Season;
  readonly dayNightPhase: DayNightPhase;
  readonly weather: Weather;
  readonly temperature: number;
  readonly waterAmount: number;
  readonly ambientPredatorLevel: AmbientPredatorLevel;
  readonly terrain: TileTerrain;
  /** Clan tile food depletion; used for stats tile-footer yield text. */
  readonly foodDepletion: number;
  /** When true, tile footer uses the traveling (no harvest) copy. */
  readonly movedThisTurn: boolean;
}

export function selectHud(state: WorldState): SetupHudView {
  const revealedTileCount = Object.values(state.tiles).filter(
    (tile) => tile.visibility === "revealed",
  ).length;
  const clanTile =
    state.tiles[tileKey(state.clan.currentTile.q, state.clan.currentTile.r)];
  const terrain = clanTile?.terrain ?? "plains";
  const foodDepletion = clanTile?.foodDepletion ?? 0;
  const season = state.season;
  const dayNightPhase = getDayNightPhaseForWeatherTurn(state.turn);
  const cycleProgress = getCycleProgressForWeatherTurn(state.turn);

  // Host overlays localized prompts via @dawn/i18n; keep English fallback here.
  let hint = "";
  switch (state.phase) {
    case "selectingTotem":
      hint = "Name your clan and confirm to choose a starting tile.";
      break;
    case "selectingStart":
      hint = "Choose where your clan should start.";
      break;
    case "settling":
      hint = "Clan established.";
      break;
    case "playing":
      hint = state.clan.movedThisTurn
        ? "End your turn when ready."
        : "Move to an adjacent tile or end your turn.";
      break;
    case "victory":
      hint = "Your clan has flourished!";
      break;
    case "defeat":
      hint = "Your clan has starved.";
      break;
  }

  return {
    phase: state.phase,
    turn: state.turn,
    clanName: state.clan.clanName,
    population: state.clan.population,
    foodSupplies: state.clan.foodSupplies,
    tileCount: Object.keys(state.tiles).length,
    revealedTileCount,
    canSelectTotem: state.phase === "selectingTotem",
    canChooseStart: state.phase === "selectingStart",
    canCompleteSettle: state.phase === "settling",
    canEndTurn: state.phase === "playing",
    hint,
    season,
    dayNightPhase,
    weather: state.clan.ambientWeather,
    temperature: resolveTemperature(
      state.turn,
      terrain,
      season,
      dayNightPhase,
      cycleProgress,
    ),
    waterAmount: getWaterAmount(terrain, season),
    ambientPredatorLevel: getTileAmbientPredatorLevel(
      terrain,
      season,
      dayNightPhase,
    ),
    terrain,
    foodDepletion,
    movedThisTurn: state.clan.movedThisTurn,
  };
}
